import { useState, useEffect, useCallback, useRef } from 'react';
import { Spin, notification, Input, Avatar } from 'antd';
import {
  ClockCircleOutlined, CompassOutlined, EnvironmentOutlined, FilterOutlined,
  HomeOutlined, LeftOutlined, MessageOutlined, MoreOutlined,
  PushpinFilled, PushpinOutlined, SearchOutlined, SendOutlined,
  StarFilled, StarOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl, socketUrl } from '../../../config';
import { io as socketIo } from 'socket.io-client';
import { M, G, BG } from '../shared/constants';
import { authHeader, useLang, playNotifSound, loadDaySet, saveDaySet } from '../shared/utils';
import { lastSeen, timeAgo, LastSeenBadge } from '../components/PostCard';
import { useMobile } from '../shared/hooks';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

export function MemberChatTab({ profile, onSwitchTab, isVisible, openChatWith, openChatWithData, onChatOpened, onNewMessage, onChatRequest }) {
  const [activeChat,    setActiveChat]    = useState('global');
  const [contacts,      setContacts]      = useState([]);
  const [globalMsgs,    setGlobalMsgs]    = useState([]);
  const [directMsgs,    setDirectMsgs]    = useState([]);
  const [recipientReadAt, setRecipientReadAt] = useState(null); // when the OTHER person last read our messages
  const [villageMsgs,   setVillageMsgs]   = useState([]);
  const [villageName,   setVillageName]   = useState(null);
  const [villageRemaining, setVillageRemaining] = useState(20);
  const [text,          setText]          = useState('');
  const [sending,       setSending]       = useState(false);
  const [remaining,     setRemaining]     = useState({ global: 30, direct: 50 });
  const [showSidebar,   setShowSidebar]   = useState(true); // always show contact list first; on mobile user picks a chat then sidebar hides
  const [reportId,      setReportId]      = useState(null);
  const [socket,        setSocket]        = useState(null);
  const [typing,        setTyping]        = useState(false);
  const [ctxMenu,       setCtxMenu]       = useState(null);
  // Online presence
  const [onlineUsers,   setOnlineUsers]   = useState([]);
  const [onlineCount,   setOnlineCount]   = useState(0);
  // Member search in sidebar
  const [searchQ,       setSearchQ]       = useState('');
  const [searchRes,     setSearchRes]     = useState([]);
  const [searching,     setSearching]     = useState(false);
  const [showAdvSearch, setShowAdvSearch] = useState(false);
  const [searchVillage, setSearchVillage] = useState('');
  const [searchDistrict,setSearchDistrict]= useState('');

  const mobile      = useMobile();
  const bottomRef   = useRef(null);
  const msgAreaRef  = useRef(null);
  const inputRef    = useRef(null);
  const searchTimer = useRef(null);
  const cachedOpenDataRef = useRef(null);
  const myId       = profile?.id;
  const myName     = profile?.name_hn || profile?.name_eng || '';
  const myPhoto    = profile?.profile_img;
  const ini = n => (n || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  // ── Refs to avoid stale closures inside socket callbacks ──
  const activeChatRef  = useRef(activeChat);
  const isVisibleRef   = useRef(isVisible);
  const myIdRef        = useRef(myId);
  useEffect(() => { activeChatRef.current = activeChat; },  [activeChat]);
  useEffect(() => { isVisibleRef.current  = isVisible; },   [isVisible]);
  useEffect(() => { myIdRef.current       = myId; },        [myId]);

  // Stable callback refs so socket handlers always call latest version
  const loadContactsRef = useRef(null);
  const loadDirectRef   = useRef(null);
  const loadGlobalRef   = useRef(null);
  const loadVillageRef  = useRef(null);

  // ── Socket connection ──
  useEffect(() => {
    const token = localStorage.getItem('memberToken');
    if (!token) return;
    const sock = socketIo(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'], // polling fallback if websocket blocked
      reconnectionAttempts: Infinity,       // keep trying forever
      reconnectionDelay: 1500,
      reconnectionDelayMax: 8000,
    });

    sock.on('connect', () => {
      sock.emit('member_presence', { status: 'online' });
    });

    // Re-fetch missed messages after reconnect
    sock.on('reconnect', () => {
      sock.emit('member_presence', { status: 'online' });
      loadContactsRef.current?.();
      loadGlobalRef.current?.();
      loadVillageRef.current?.();
      const cur = activeChatRef.current;
      if (cur !== 'global' && cur !== 'village') loadDirectRef.current?.(cur);
    });

    sock.on('member_presence_update', ({ userId, online, name, photo, village }) => {
      setOnlineUsers(prev => {
        if (online) return prev.some(u => u.id === userId) ? prev : [...prev, { id: userId, name, photo, village }];
        return prev.filter(u => u.id !== userId);
      });
      setOnlineCount(prev => online ? prev + 1 : Math.max(0, prev - 1));
      setContacts(prev => prev.map(c =>
        c.contact_id === userId
          ? { ...c, last_active_at: online ? new Date().toISOString() : c.last_active_at }
          : c
      ));
    });

    sock.on('member_online_list', ({ users, count }) => {
      setOnlineUsers(users || []);
      setOnlineCount(count || (users || []).length);
    });

    sock.on('member_direct_msg', msg => {
      const fromId    = String(msg.from_user_id);
      const curChat   = activeChatRef.current;
      const chatOpen  = isVisibleRef.current;

      if (curChat === fromId) {
        // User is already viewing this chat — add message immediately + mark read
        setDirectMsgs(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
        axios.patch(`${baseUrl}/member/chat/direct/${fromId}/read`, {}, { headers: authHeader() }).catch(() => {});
      } else {
        // User is on a different chat — update contact sidebar badge instantly (no API call needed)
        setContacts(prev => {
          const exists = prev.some(c => c.contact_id === fromId);
          if (exists) {
            return prev.map(c => c.contact_id === fromId
              ? { ...c, unread_count: (c.unread_count || 0) + 1, last_message: msg.message, last_message_at: msg.created_at || new Date().toISOString() }
              : c
            );
          }
          // Brand-new contact not yet in list — prepend it
          return [{ contact_id: fromId, name: msg.sender_name, photo: msg.sender_photo, unread_count: 1, last_message: msg.message, conn_status: 'accepted' }, ...prev];
        });
      }

      playNotifSound('direct');

      // Notification toast only if not currently viewing that chat
      if (curChat !== fromId) {
        if (!chatOpen && onNewMessage) onNewMessage({ sender_name: msg.sender_name, sender_photo: msg.sender_photo, message: msg.message, from_user_id: msg.from_user_id });
        notification.open({
          message: <span style={{ fontWeight: 700 }}>{msg.sender_name || 'New message'}</span>,
          description: (
            <div>
              <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>
                {(msg.message || '').slice(0, 80)}{msg.message?.length > 80 ? '…' : ''}
              </div>
              <button onClick={() => {
                notification.destroy(`dm_${msg.id}`);
                if (onSwitchTab) onSwitchTab('chat');
              }} style={{ background: M, color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                💬 Reply
              </button>
            </div>
          ),
          icon: msg.sender_photo
            ? <img src={msg.sender_photo} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
            : <MessageOutlined style={{ color: M }} />,
          placement: 'topRight',
          duration: 7,
          key: `dm_${msg.id}`,
        });
      }
    });

    sock.on('member_chat_request', ({ sender_name, sender_photo, from_user_id, message } = {}) => {
      const fromId = String(from_user_id);
      // Immediately insert pending contact into sidebar without waiting for API
      setContacts(prev => {
        if (prev.some(c => c.contact_id === fromId)) return prev;
        return [{ contact_id: fromId, name: sender_name, photo: sender_photo, conn_status: 'pending', unread_count: 1, last_message: message || '' }, ...prev];
      });
      playNotifSound('direct');
      const chatOpen = isVisibleRef.current;
      if (!chatOpen && onNewMessage) onNewMessage({ sender_name, sender_photo, message: message || 'Message request', from_user_id });
      if (onChatRequest) onChatRequest({ sender_name, sender_photo, from_user_id, message });
      // Background refresh for full accuracy
      loadContactsRef.current?.();
    });

    sock.on('member_global_msg', msg => {
      setGlobalMsgs(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
      setTyping(false);
      if (String(msg.from_user_id) !== String(myIdRef.current)) {
        playNotifSound('message');
        const chatOpen = isVisibleRef.current;
        if (!chatOpen && onNewMessage) onNewMessage();
        notification.open({
          message: <span style={{ fontWeight: 700 }}>Community · {msg.sender_name}</span>,
          description: (
            <div>
              <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>
                {(msg.message || '').slice(0, 70)}{msg.message?.length > 70 ? '…' : ''}
              </div>
              <button onClick={() => { notification.destroy(`gm_${msg.id}`); if (onSwitchTab) onSwitchTab('chat'); }}
                style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                💬 Reply
              </button>
            </div>
          ),
          icon: <span style={{ fontSize: 18 }}>🌐</span>,
          placement: 'topRight',
          duration: 4,
          key: `gm_${msg.id}`,
        });
      }
    });

    sock.on('member_village_msg', msg => {
      setVillageMsgs(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
      if (String(msg.from_user_id) !== String(myIdRef.current)) playNotifSound('message');
    });

    sock.on('member_chat_accepted', ({ by_user_id, by_name, by_photo } = {}) => {
      // Immediately update contact status in sidebar — no API wait needed
      if (by_user_id) {
        setContacts(prev => prev.map(c =>
          c.contact_id === String(by_user_id) ? { ...c, conn_status: 'accepted' } : c
        ));
      }
      loadContactsRef.current?.();
      playNotifSound('direct');
      notification.success({
        message: (
          <span style={{ fontWeight: 700 }}>
            {by_photo && <img src={by_photo} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', marginRight: 7, verticalAlign: 'middle' }} />}
            {by_name || 'सदस्य'} ने chat accept किया! ✅
          </span>
        ),
        description: (
          <div>
            <div style={{ fontSize: 12, color: '#374151', marginBottom: 6 }}>अब आप बात कर सकते हैं।</div>
            <button onClick={() => {
              notification.destroy('chat_accepted');
              if (by_user_id) {
                if (by_photo || by_name) {
                  // open their chat directly
                }
              }
              if (onSwitchTab) onSwitchTab('chat');
            }} style={{ background: '#15803d', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              💬 Chat Now
            </button>
          </div>
        ),
        placement: 'topRight',
        duration: 8,
        key: 'chat_accepted',
      });
    });

    // Contacts list needs refresh (e.g. after accepting a request from another device)
    sock.on('member_contacts_refresh', () => {
      loadContactsRef.current?.();
    });

    // Sender gets notified when their request is declined
    sock.on('member_chat_declined', ({ by_user_id } = {}) => {
      if (by_user_id) {
        setContacts(prev => prev.map(c =>
          c.contact_id === String(by_user_id) ? { ...c, conn_status: 'declined' } : c
        ));
      }
      notification.warning({
        message: 'Message Request Declined',
        description: 'सदस्य ने आपका chat request decline किया।',
        placement: 'topRight',
        duration: 5,
      });
    });

    sock.on('member_msg_deleted', ({ id }) => {
      setGlobalMsgs(prev => prev.map(m => m.id === id ? { ...m, is_deleted: 1 } : m));
      setDirectMsgs(prev => prev.map(m => m.id === id ? { ...m, is_deleted: 1 } : m));
    });

    sock.on('member_typing', () => {
      setTyping(true);
      setTimeout(() => setTyping(false), 2500);
    });

    sock.on('member_msgs_read', ({ by, read_at }) => {
      setRecipientReadAt(prev => {
        if (!prev || new Date(read_at) > new Date(prev)) return read_at;
        return prev;
      });
    });

    setSocket(sock);
    return () => sock.disconnect();
  }, []); // eslint-disable-line

  // ── Scroll to bottom — instant on tab switch, smooth on new message ──
  const scrollToBottom = (smooth = true) => {
    if (msgAreaRef.current) {
      msgAreaRef.current.scrollTo({ top: msgAreaRef.current.scrollHeight, behavior: smooth ? 'smooth' : 'instant' });
    }
  };
  useEffect(() => { scrollToBottom(true); }, [globalMsgs, directMsgs, villageMsgs]);
  useEffect(() => { scrollToBottom(false); }, [activeChat]); // instant jump on tab switch
  useEffect(() => { if (isVisible) scrollToBottom(false); }, [isVisible]); // scroll when tab becomes visible
  useEffect(() => {
    if (openChatWith) {
      if (openChatWithData) cachedOpenDataRef.current = openChatWithData; // save before parent clears it
      setActiveChat(openChatWith);
      setShowSidebar(false);
      if (onChatOpened) onChatOpened();
    }
  }, [openChatWith]); // eslint-disable-line

  const loadContacts = useCallback(async () => {
    try {
      const r = await axios.get(`${baseUrl}/member/chat/contacts`, { headers: authHeader() });
      if (r.data.status) setContacts(r.data.result || []);
    } catch {}
  }, []);

  const loadOnlineUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${baseUrl}/member/chat/online-users`, { headers: authHeader() });
      if (r.data.status) {
        setOnlineUsers(r.data.result || []);
        setOnlineCount(r.data.count ?? (r.data.result || []).length);
      }
    } catch {}
  }, []);

  const loadGlobal = useCallback(async () => {
    try {
      const r = await axios.get(`${baseUrl}/member/chat/global`, { headers: authHeader() });
      if (r.data.status) {
        setGlobalMsgs(r.data.result || []);
        setRemaining(p => ({ ...p, global: r.data.remaining ?? 30 }));
      }
    } catch {}
  }, []);

  const loadVillage = useCallback(async () => {
    try {
      const r = await axios.get(`${baseUrl}/member/chat/village`, { headers: authHeader() });
      if (r.data.status) {
        setVillageMsgs(r.data.result || []);
        setVillageName(r.data.village || null);
        setVillageRemaining(r.data.remaining ?? 20);
      }
    } catch {}
  }, []);

  const loadDirect = useCallback(async (contactId) => {
    try {
      const r = await axios.get(`${baseUrl}/member/chat/direct/${contactId}`, { headers: authHeader() });
      if (r.data.status) {
        setDirectMsgs(r.data.result || []);
        setRemaining(p => ({ ...p, direct: r.data.remaining ?? 50 }));
        // Store when recipient last read our messages (for blue ticks)
        if (r.data.recipient_read_at) setRecipientReadAt(r.data.recipient_read_at);
      }
      // Mark messages as read — clear per-conversation badge immediately
      axios.patch(`${baseUrl}/member/chat/direct/${contactId}/read`, {}, { headers: authHeader() }).catch(() => {});
      setContacts(prev =>
        prev.some(c => c.contact_id === contactId && (c.unread_count || 0) > 0)
          ? prev.map(c => c.contact_id === contactId ? { ...c, unread_count: 0 } : c)
          : prev
      );
    } catch {}
  }, []);

  // Wire stable refs so socket handlers always call the latest functions
  useEffect(() => { loadContactsRef.current = loadContacts; }, [loadContacts]);
  useEffect(() => { loadDirectRef.current   = loadDirect;   }, [loadDirect]);
  useEffect(() => { loadGlobalRef.current   = loadGlobal;   }, [loadGlobal]);
  useEffect(() => { loadVillageRef.current  = loadVillage;  }, [loadVillage]);

  useEffect(() => {
    loadContacts();
    loadGlobal();
    loadVillage();
    loadOnlineUsers();
    // Online list: poll every 30s
    const onlineTimer = setInterval(loadOnlineUsers, 30000);
    return () => clearInterval(onlineTimer);
  }, [loadContacts, loadGlobal, loadVillage, loadOnlineUsers]);

  // Refresh when user switches TO the chat tab — catches messages missed while on other tabs
  const prevVisible = useRef(false);
  useEffect(() => {
    if (isVisible && !prevVisible.current) {
      loadContacts();
      loadGlobal();
      loadVillage();
      if (activeChat !== 'global' && activeChat !== 'village') loadDirect(activeChat);
    }
    prevVisible.current = isVisible;
  }, [isVisible]); // eslint-disable-line

  // ── Polling fallback — catches messages if socket delivery failed ──
  useEffect(() => {
    if (!isVisible) return;
    const pollTimer = setInterval(() => {
      loadContacts();
      const cur = activeChatRef.current;
      if (cur !== 'global' && cur !== 'village') loadDirect(cur);
      else if (cur === 'global') loadGlobal();
    }, 15000);
    return () => clearInterval(pollTimer);
  }, [isVisible, loadContacts, loadDirect, loadGlobal]);

  useEffect(() => {
    if (activeChat !== 'global' && activeChat !== 'village') loadDirect(activeChat);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [activeChat, loadDirect]);

  const sendMessage = async () => {
    const msg = text.trim();
    if (!msg || sending) return;
    setSending(true);
    try {
      if (activeChat === 'global') {
        const r = await axios.post(`${baseUrl}/member/chat/global/send`, { message: msg }, { headers: authHeader() });
        if (r.data.status) {
          // Add immediately — socket echo will be deduped
          setGlobalMsgs(prev => [...prev, r.data.message]);
          setRemaining(p => ({ ...p, global: r.data.remaining ?? 0 }));
          setText('');
        } else {
          notification.warning({ message: r.data.message, placement: 'topRight' });
        }
      } else if (activeChat === 'village') {
        const r = await axios.post(`${baseUrl}/member/chat/village/send`, { message: msg }, { headers: authHeader() });
        if (r.data.status) {
          setVillageMsgs(prev => [...prev, r.data.message]);
          setVillageRemaining(r.data.remaining ?? 0);
          setText('');
        } else {
          notification.warning({ message: r.data.message, placement: 'topRight' });
        }
      } else {
        const r = await axios.post(`${baseUrl}/member/chat/direct/${activeChat}/send`, { message: msg }, { headers: authHeader() });
        if (r.data.status) {
          setDirectMsgs(prev => [...prev, r.data.message]);
          setRemaining(p => ({ ...p, direct: r.data.remaining ?? 50 }));
          setText('');
          loadContacts();
        } else {
          notification.warning({ message: r.data.message, placement: 'topRight' });
        }
      }
    } catch (e) {
      notification.error({ message: e.response?.data?.message || 'Error' });
    } finally { setSending(false); }
  };

  const deleteMsg = async (id) => {
    try {
      await axios.delete(`${baseUrl}/member/chat/message/${id}`, { headers: authHeader() });
    } catch (e) {
      notification.error({ message: e.response?.data?.message || '10 मिनट की limit' });
    }
  };

  const reportMsg = async (id) => {
    try {
      await axios.post(`${baseUrl}/member/chat/report/${id}`, { reason: 'अनुचित सामग्री' }, { headers: authHeader() });
      notification.success({ message: 'Report दर्ज की गई।' });
      setReportId(null);
    } catch {}
  };

  // ── Member search ──
  const doSearch = async (q = searchQ, vil = searchVillage, dist = searchDistrict) => {
    if (!q.trim() && !vil.trim() && !dist.trim()) { setSearchRes([]); return; }
    setSearching(true);
    try {
      const params = new URLSearchParams({ limit: 20 });
      if (q.trim())    params.set('search',   q.trim());
      if (vil.trim())  params.set('village',  vil.trim());
      if (dist.trim()) params.set('district', dist.trim());
      const r = await axios.get(`${baseUrl}/member/member-search?${params}`, { headers: authHeader() });
      if (r.data.status) setSearchRes(r.data.result || []);
    } catch {} finally { setSearching(false); }
  };

  // Open chat with any member (creates pending request on first message)
  const openChat = (memberId) => {
    setActiveChat(memberId);
    setSearchQ('');
    setSearchRes([]);
    // On mobile: hide sidebar to show chat full-screen
    if (mobile) setShowSidebar(false);
  };

  // Toggle pin or favourite
  const togglePref = async (contactId, field) => {
    try {
      const r = await axios.patch(`${baseUrl}/member/chat/prefs/${contactId}`, { field }, { headers: authHeader() });
      if (r.data.status) {
        setContacts(prev => prev.map(c =>
          c.contact_id === contactId
            ? { ...c, is_pinned: r.data.is_pinned, is_favourite: r.data.is_favourite }
            : c
        ));
        // Re-sort locally: pinned + online + favourite
        setContacts(prev => [...prev].sort((a, b) => {
          if (b.is_pinned   !== a.is_pinned)   return b.is_pinned   - a.is_pinned;
          const aOnline = lastSeen(a.last_active_at)?.online ? 0 : 1;
          const bOnline = lastSeen(b.last_active_at)?.online ? 0 : 1;
          if (bOnline !== aOnline) return aOnline - bOnline;
          if (b.is_favourite !== a.is_favourite) return b.is_favourite - a.is_favourite;
          return 0;
        }));
      }
    } catch {}
    setCtxMenu(null);
  };

  // Accept a pending chat request
  const acceptRequest = async (fromUserId, contactData) => {
    try {
      await axios.patch(`${baseUrl}/member/chat/request/${fromUserId}/accept`, {}, { headers: authHeader() });
      notification.success({ message: '✅ Chat accepted! You can now message.', placement: 'topRight', duration: 3 });
      // Set fallback data so chatbox header shows name/photo immediately
      if (contactData) {
        cachedOpenDataRef.current = { id: fromUserId, name: contactData.name, photo: contactData.photo, village: contactData.village };
      }
      // Reload contacts then open the chat immediately
      const r = await axios.get(`${baseUrl}/member/chat/contacts`, { headers: authHeader() });
      if (r.data.status) {
        setContacts(r.data.result || []);
        // Open the chat right away — no reload needed
        setActiveChat(String(fromUserId));
        if (mobile) setShowSidebar(false);
      }
    } catch {}
  };

  // Decline a pending chat request
  const declineRequest = async (fromUserId) => {
    try {
      await axios.patch(`${baseUrl}/member/chat/request/${fromUserId}/decline`, {}, { headers: authHeader() });
      loadContacts();
    } catch {}
  };

  const messages      = activeChat === 'global' ? globalMsgs : activeChat === 'village' ? villageMsgs : directMsgs;
  const activeContactFromList = contacts.find(c => c.contact_id === activeChat);
  // Fall back to data passed from the Home online-users strip while contacts list loads.
  // Use cachedOpenDataRef because the parent clears openChatWithData immediately after onChatOpened().
  const _fallbackData = openChatWithData || (cachedOpenDataRef.current && String(cachedOpenDataRef.current.id) === String(activeChat) ? cachedOpenDataRef.current : null);
  const activeContact = activeContactFromList || (
    _fallbackData
      ? { contact_id: activeChat, name: _fallbackData.name, photo: _fallbackData.photo, village: _fallbackData.village, last_active_at: new Date().toISOString() }
      : null
  );
  const connStatus    = activeChat === 'global' || activeChat === 'village' ? null : (activeContact?.conn_status || 'pending');
  const isPendingSent = connStatus === 'pending' && activeContact?.from_user_id === myId;
  const remLeft       = activeChat === 'global' ? remaining.global : activeChat === 'village' ? villageRemaining : remaining.direct;
  const remMax        = activeChat === 'global' ? 30 : activeChat === 'village' ? 20 : 50;

  const MsgBubble = ({ m }) => {
    const isMine = m.from_user_id === myId;
    if (m.is_deleted) return (
      <div style={{ textAlign: isMine ? 'right' : 'left', margin: '4px 12px', color: '#9ca3af', fontSize: 11, fontStyle: 'italic' }}>
        🚫 यह संदेश हटा दिया गया
      </div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: isMine ? 'row-reverse' : 'row',
        alignItems: 'flex-end', gap: 6, margin: '6px 10px' }}>
        {/* Avatar */}
        {!isMine && (
          m.sender_photo
            ? <img src={m.sender_photo} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', flexShrink: 0 }} />
            : <div style={{ width: 28, height: 28, borderRadius: '50%', background: M, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: '#fff' }}>{ini(m.sender_name)}</div>
        )}
        <div style={{ maxWidth: '72%' }}>
          {/* Sender name (global and village, others' messages) */}
          {!isMine && (activeChat === 'global' || activeChat === 'village') && (
            <div style={{ fontSize: 10, fontWeight: 700, color: M, marginBottom: 2, paddingLeft: 2 }}>
              {m.sender_name}
              {m.sender_village && activeChat === 'global' && <span style={{ color: '#9ca3af', fontWeight: 400 }}> · {m.sender_village}</span>}
            </div>
          )}
          {/* Bubble */}
          <div
            onDoubleClick={() => isMine && setReportId(reportId === m.id ? null : m.id)}
            style={{
              background: isMine ? M : '#f0f2f5',
              color: isMine ? '#fff' : '#1c1e21',
              borderRadius: isMine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              padding: '8px 12px', fontSize: 13, lineHeight: 1.5,
              cursor: 'default', wordBreak: 'break-word',
            }}>
            {m.message}
          </div>
          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2, textAlign: isMine ? 'right' : 'left', paddingLeft: 4, display: 'flex', alignItems: 'center', justifyContent: isMine ? 'flex-end' : 'flex-start', gap: 4 }}>
            {timeAgo(m.created_at)}
            {/* Read receipts — only for my messages in direct chat */}
            {isMine && activeChat !== 'global' && activeChat !== 'village' && (() => {
              const isRead = recipientReadAt && new Date(m.created_at) <= new Date(recipientReadAt);
              return (
                <span title={isRead ? 'पढ़ लिया गया' : 'भेजा गया'} style={{ fontSize: 11, letterSpacing: -2, color: isRead ? '#3b82f6' : '#9ca3af', fontWeight: 700 }}>
                  {isRead ? '✓✓' : '✓'}
                </span>
              );
            })()}
            {isMine && (
              <button onClick={() => deleteMsg(m.id)}
                style={{ background: 'none', border: 'none', color: '#d1d5db', fontSize: 10, cursor: 'pointer', marginLeft: 2 }}>
                हटाएं
              </button>
            )}
            {!isMine && (
              <button onClick={() => setReportId(reportId === m.id ? null : m.id)}
                style={{ background: 'none', border: 'none', color: '#d1d5db', fontSize: 10, cursor: 'pointer', marginLeft: 6 }}>
                🚩
              </button>
            )}
          </div>
          {reportId === m.id && !isMine && (
            <div style={{ background: '#fff3cd', borderRadius: 8, padding: '6px 10px', marginTop: 4, fontSize: 11 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>इस message को report करें?</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => reportMsg(m.id)} style={{ background: M, color: '#fff', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}>Report</button>
                <button onClick={() => setReportId(null)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}>रद्द</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      display: 'flex',
      height: mobile ? 'calc(100vh - 130px)' : 'calc(100vh - 160px)',
      minHeight: 400,
      background: '#f0f2f5',
      borderRadius: mobile ? 0 : 14,
      overflow: 'hidden',
      border: mobile ? 'none' : '1px solid #e4e6eb',
    }}>

      {/* ── Sidebar — full-screen on mobile when no chat open ── */}
      {(showSidebar || (mobile && activeChat === null)) && (
        <div style={{
          width: mobile ? '100%' : 300,
          flexShrink: 0, background: '#fff',
          borderRight: mobile ? 'none' : '1px solid #e4e6eb',
          display: 'flex', flexDirection: 'column',
          // On mobile: hide when chat is active
          display: mobile && !showSidebar ? 'none' : 'flex',
        }}>
          {/* Sidebar header + search */}
          <div style={{ padding: '12px 12px 10px', borderBottom: '1px solid #f0f0f0' }}>
            {/* Title + online count */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#1c1e21', display: 'flex', alignItems: 'center', gap: 6 }}><MessageOutlined style={{ color: M }} /> Messenger</div>
              {onlineCount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#dcfce7', borderRadius: 20, padding: '2px 8px' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block', boxShadow: '0 0 0 2px #bbf7d0' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d' }}>{onlineCount} online</span>
                </div>
              )}
            </div>
            {/* Search box + filter toggle */}
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <SearchOutlined style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 13, pointerEvents: 'none' }} />
                  <input
                    value={searchQ}
                    onChange={e => {
                      setSearchQ(e.target.value);
                      clearTimeout(searchTimer.current);
                      searchTimer.current = setTimeout(() => doSearch(e.target.value, searchVillage, searchDistrict), 400);
                    }}
                    placeholder="Search members..."
                    style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: 20, border: '1px solid #e4e6eb', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>
                <button
                  onClick={() => setShowAdvSearch(s => !s)}
                  title="Filter by village / district"
                  style={{ background: showAdvSearch ? M : '#f3f4f6', border: 'none', borderRadius: 20, padding: '0 10px', cursor: 'pointer', color: showAdvSearch ? '#fff' : '#6b7280', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                  <FilterOutlined style={{ fontSize: 13 }} />
                </button>
              </div>
              {searching && <Spin size="small" style={{ position: 'absolute', right: 46, top: 8 }} />}
            </div>
            {/* Advanced filters */}
            {showAdvSearch && (
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 5 }}>
                <div style={{ position: 'relative' }}>
                  <HomeOutlined style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 12, pointerEvents: 'none' }} />
                  <input
                    value={searchVillage}
                    onChange={e => {
                      setSearchVillage(e.target.value);
                      clearTimeout(searchTimer.current);
                      searchTimer.current = setTimeout(() => doSearch(searchQ, e.target.value, searchDistrict), 400);
                    }}
                    placeholder="Village name..."
                    style={{ width: '100%', padding: '6px 10px 6px 28px', borderRadius: 8, border: '1px solid #e4e6eb', fontSize: 11, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>
                <div style={{ position: 'relative' }}>
                  <CompassOutlined style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 12, pointerEvents: 'none' }} />
                  <input
                    value={searchDistrict}
                    onChange={e => {
                      setSearchDistrict(e.target.value);
                      clearTimeout(searchTimer.current);
                      searchTimer.current = setTimeout(() => doSearch(searchQ, searchVillage, e.target.value), 400);
                    }}
                    placeholder="District name..."
                    style={{ width: '100%', padding: '6px 10px 6px 28px', borderRadius: 8, border: '1px solid #e4e6eb', fontSize: 11, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>
                {(searchQ || searchVillage || searchDistrict) && (
                  <button onClick={() => { setSearchQ(''); setSearchVillage(''); setSearchDistrict(''); setSearchRes([]); }}
                    style={{ background: 'none', border: 'none', fontSize: 11, color: '#9ca3af', cursor: 'pointer', textAlign: 'left', padding: 0 }}>
                    ✕ Clear filters
                  </button>
                )}
              </div>
            )}
            {/* Search results */}
            {searchRes.length > 0 && (
              <div style={{ marginTop: 6, background: '#fff', border: '1px solid #e4e6eb', borderRadius: 10, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 260, overflowY: 'auto' }}>
                <div style={{ padding: '5px 10px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #f3f4f6' }}>
                  {searchRes.length} result{searchRes.length !== 1 ? 's' : ''} found
                </div>
                {searchRes.map(m => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                    onClick={() => { cachedOpenDataRef.current = { id: m.id, name: m.name, photo: m.photo, village: m.village }; openChat(m.id); }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      {m.photo
                        ? <img src={m.photo} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }} />
                        : <div style={{ width: 30, height: 30, borderRadius: '50%', background: M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: '#fff' }}>{ini(m.name)}</div>
                      }
                      {lastSeen(m.last_active_at)?.online && (
                        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 8, height: 8, borderRadius: '50%', background: '#22c55e', border: '2px solid #fff' }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#1c1e21', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                      <div style={{ fontSize: 10, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {[m.village, m.district].filter(Boolean).join(', ') || ''}
                        {lastSeen(m.last_active_at)?.online && <span style={{ color: '#15803d', fontWeight: 700, marginLeft: 4 }}>· Online</span>}
                      </div>
                    </div>
                    <span style={{ fontSize: 10, color: M, fontWeight: 700, flexShrink: 0 }}>Message →</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Global chat button */}
          <button onClick={() => { setActiveChat('global'); if (mobile) setShowSidebar(false); }} style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            padding: '10px 14px', cursor: 'pointer', border: 'none', textAlign: 'left',
            background: activeChat === 'global' ? '#f0f2f5' : '#fff',
            borderBottom: `2px solid ${activeChat === 'global' ? M : '#f0f2f5'}`,
            transition: 'background 0.15s',
          }}>
            {/* Avatar with online ring */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ width: 42, height: 42, borderRadius: '50%', background: `linear-gradient(135deg,${M},#b71c1c)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, border: `2px solid ${activeChat === 'global' ? M : '#e4e6eb'}` }}>🌐</div>
              {onlineCount > 0 && (
                <div style={{ position: 'absolute', bottom: 0, right: 0, background: '#22c55e', color: '#fff', fontSize: 8, fontWeight: 800, borderRadius: 8, padding: '1px 4px', border: '1.5px solid #fff', lineHeight: 1.4 }}>{onlineCount}</div>
              )}
            </div>
            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#1c1e21' }}>RAJPUROHIT Community</span>
                <span style={{ fontSize: 8, fontWeight: 700, background: M, color: '#FFD700', borderRadius: 4, padding: '1px 5px', letterSpacing: 0.3, flexShrink: 0 }}>GROUP</span>
              </div>
              <div style={{ fontSize: 11, color: '#65676b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {globalMsgs.length > 0
                  ? (() => { const last = globalMsgs[globalMsgs.length - 1]; return last.is_deleted ? '🚫 संदेश हटाया गया' : `${(last.sender_name || '').split(' ')[0]}: ${last.message}`; })()
                  : 'सभी सदस्यों का समूह · 24 घंटे का इतिहास'
                }
              </div>
            </div>
            {/* Chevron / active indicator */}
            <div style={{ flexShrink: 0, color: activeChat === 'global' ? M : '#d1d5db', fontSize: 14, fontWeight: 900 }}>›</div>
          </button>

          {/* Village group chat button */}
          {villageName && (
            <button onClick={() => { setActiveChat('village'); if (mobile) setShowSidebar(false); }} style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              padding: '10px 14px', cursor: 'pointer', border: 'none', textAlign: 'left',
              background: activeChat === 'village' ? '#f0f2f5' : '#fff',
              borderBottom: `2px solid ${activeChat === 'village' ? M : '#f0f2f5'}`,
            }}>
              <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#15803d',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🏘️</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1c1e21' }}>{villageName} ग्रुप</div>
                <div style={{ fontSize: 11, color: '#65676b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {villageMsgs.length > 0
                    ? (() => { const last = villageMsgs[villageMsgs.length-1]; return `${(last.sender_name||'').split(' ')[0]}: ${last.message}`; })()
                    : 'गाँव के सदस्यों से बात करें • 24 घंटे'}
                </div>
              </div>
              <div style={{ flexShrink: 0, color: activeChat === 'village' ? M : '#d1d5db', fontSize: 14, fontWeight: 900 }}>›</div>
            </button>
          )}

          {/* Direct contacts + online section */}
          <div style={{ flex: 1, overflowY: 'auto' }}>

            {/* Online Now section */}
            {onlineUsers.filter(u => u.id !== myId).length > 0 && (
              <div>
                <div style={{ padding: '8px 14px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Online Now · {onlineUsers.filter(u => u.id !== myId).length}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, padding: '4px 14px 8px', overflowX: 'auto', scrollbarWidth: 'none' }}>
                  {onlineUsers.filter(u => u.id !== myId).slice(0, 12).map(u => (
                    <div key={u.id} onClick={() => { cachedOpenDataRef.current = { id: u.id, name: u.name, photo: u.photo, village: u.village }; openChat(u.id); }}
                      title={`${u.name}${u.village ? ' · ' + u.village : ''}`}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: 'pointer', flexShrink: 0, width: 48 }}>
                      <div style={{ position: 'relative' }}>
                        {u.photo
                          ? <img src={u.photo} alt="" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: '2px solid #22c55e' }} />
                          : <div style={{ width: 38, height: 38, borderRadius: '50%', background: M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', border: '2px solid #22c55e' }}>{ini(u.name)}</div>
                        }
                        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, borderRadius: '50%', background: '#22c55e', border: '2px solid #fff' }} />
                      </div>
                      <div style={{ fontSize: 9, fontWeight: 600, color: '#374151', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                        {(u.name || '').split(' ')[0]}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ height: 1, background: '#f0f2f5', margin: '0 14px' }} />
              </div>
            )}

            <div style={{ padding: '8px 14px 4px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#65676b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Direct Messages</div>
            </div>
            {contacts.length === 0 && (
              <div style={{ padding: '8px 14px', fontSize: 11, color: '#9ca3af' }}>
                Search &amp; connect with members above to start chatting
              </div>
            )}
            {contacts.map(c => {
              const isPendingReceived = c.conn_status === 'pending' && c.from_user_id !== myId;
              const isPendingSent     = c.conn_status === 'pending' && c.from_user_id === myId;
              return (
                <div key={c.contact_id}>
                  {/* Pending received request — show Accept/Decline */}
                  {isPendingReceived ? (
                    <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f2f5', background: '#fffbeb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        {c.photo
                          ? <img src={c.photo} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                          : <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{ini(c.name)}</div>
                        }
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#1c1e21' }}>{c.name}</div>
                          <div style={{ fontSize: 10, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <MessageOutlined style={{ fontSize: 10, marginRight: 3 }} />{c.last_message || 'Message request'}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => acceptRequest(c.from_user_id, c)}
                          style={{ flex: 1, background: '#15803d', color: '#fff', border: 'none', borderRadius: 8, padding: '5px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                          ✓ Accept
                        </button>
                        <button onClick={() => { declineRequest(c.from_user_id); }}
                          style={{ flex: 1, background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: 8, padding: '5px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                          ✕ Decline
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Accepted or pending-sent — clickable row */
                    <div style={{ position: 'relative' }}>
                      <div
                        onClick={() => { setActiveChat(c.contact_id); if (mobile) setShowSidebar(false); }}
                        onContextMenu={e => { e.preventDefault(); setCtxMenu({ contactId: c.contact_id, x: e.clientX, y: e.clientY }); }}
                        role="button" tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && setActiveChat(c.contact_id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                          padding: '8px 10px 8px 14px', cursor: 'pointer',
                          background: activeChat === c.contact_id ? '#f0f2f5' : '#fff',
                          borderBottom: '1px solid #f9fafb', userSelect: 'none',
                        }}>
                        {/* Avatar + online dot */}
                        <div style={{ position: 'relative', flexShrink: 0 }}>
                          {c.photo
                            ? <img src={c.photo} alt="" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', opacity: isPendingSent ? 0.6 : 1 }} />
                            : <div style={{ width: 38, height: 38, borderRadius: '50%', background: isPendingSent ? '#d1d5db' : M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff' }}>{ini(c.name)}</div>
                          }
                          {lastSeen(c.last_active_at)?.online && (
                            <div style={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, borderRadius: '50%', background: '#22c55e', border: '2px solid #fff' }} />
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {c.is_pinned    === 1 && <PushpinFilled style={{ fontSize: 10, color: M }} />}
                            {c.is_favourite === 1 && <StarFilled    style={{ fontSize: 10, color: '#f59e0b' }} />}
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1c1e21', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{c.name}</div>
                            {c.unread_count > 0 && (
                              <span style={{ background: M, color: '#fff', borderRadius: '50%', minWidth: 18, height: 18, fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: '0 4px' }}>{c.unread_count}</span>
                            )}
                          </div>
                          {c.village && (
                            <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 1, display: 'flex', alignItems: 'center', gap: 3 }}>
                              <EnvironmentOutlined style={{ fontSize: 9 }} /> {c.village}
                            </div>
                          )}
                          <div style={{ fontSize: 11, color: isPendingSent ? '#f59e0b' : '#65676b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {isPendingSent ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><ClockCircleOutlined style={{ fontSize: 10 }} /> Waiting for reply…</span> : (c.last_message || '')}
                          </div>
                        </div>
                        {/* 3-dot menu trigger — now a div, not button, to avoid nesting */}
                        <div
                          onClick={e => { e.stopPropagation(); setCtxMenu(ctxMenu?.contactId === c.contact_id ? null : { contactId: c.contact_id, x: 0, y: 0, inline: true }); }}
                          style={{ color: '#9ca3af', fontSize: 14, cursor: 'pointer', padding: '4px 6px', borderRadius: 6, flexShrink: 0, lineHeight: 1 }}>
                          <MoreOutlined style={{ fontSize: 16 }} />
                        </div>
                      </div>

                      {/* Inline context menu */}
                      {ctxMenu?.contactId === c.contact_id && ctxMenu.inline && (
                        <div style={{ position: 'absolute', right: 10, top: 36, background: '#fff', border: '1px solid #e4e6eb', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 100, minWidth: 140, padding: '4px 0' }}>
                          <button onClick={() => togglePref(c.contact_id, 'is_pinned')} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 12, cursor: 'pointer', textAlign: 'left', color: '#1c1e21' }}>
                            <PushpinOutlined /> {c.is_pinned ? 'Unpin' : 'Pin to top'}
                          </button>
                          <button onClick={() => togglePref(c.contact_id, 'is_favourite')} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 12, cursor: 'pointer', textAlign: 'left', color: '#1c1e21' }}>
                            <StarOutlined /> {c.is_favourite ? 'Unfavourite' : 'Mark favourite'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Chat window — hidden on mobile when sidebar is shown ── */}
      <div style={{ flex: 1, display: mobile && showSidebar ? 'none' : 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Header */}
        <div style={{ padding: mobile ? '10px 12px' : '10px 14px', background: '#fff', borderBottom: '1px solid #e4e6eb', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <button
            onClick={() => { if (mobile) { setShowSidebar(true); } else { setShowSidebar(s => !s); } }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#65676b', padding: '0 4px', display: 'flex', alignItems: 'center' }}>
            {mobile ? <LeftOutlined style={{ fontSize: 18 }} /> : <span style={{ fontSize: 16 }}>☰</span>}
          </button>
          {activeChat === 'global' ? (
            <>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🌐</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#1c1e21' }}>RAJPUROHIT Community</div>
                <div style={{ fontSize: 11, color: '#65676b', display: 'flex', alignItems: 'center', gap: 6 }}>
                  All members · 24hr history
                  {onlineCount > 0 && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: '#dcfce7', borderRadius: 10, padding: '1px 6px' }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d' }}>{onlineCount} online</span>
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : activeChat === 'village' ? (
            <>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#15803d',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🏘️</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#1c1e21' }}>{villageName} ग्रुप</div>
                <div style={{ fontSize: 11, color: '#65676b' }}>Village group • 24hr history</div>
              </div>
            </>
          ) : (
            <>
              <div style={{ position: 'relative' }}>
                {activeContact?.photo
                  ? <img src={activeContact.photo} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                  : <div style={{ width: 36, height: 36, borderRadius: '50%', background: M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#fff' }}>{ini(activeContact?.name)}</div>
                }
                {lastSeen(activeContact?.last_active_at)?.online && (
                  <div style={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, borderRadius: '50%', background: '#22c55e', border: '2px solid #fff' }} />
                )}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#1c1e21' }}>{activeContact?.name}</div>
                <div style={{ fontSize: 11, color: '#65676b' }}>
                  <LastSeenBadge ts={activeContact?.last_active_at} /> · 48 घंटे की history
                </div>
              </div>
            </>
          )}
          {/* Remaining count */}
          <div style={{ marginLeft: 'auto', textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: remLeft <= 2 ? '#dc2626' : '#65676b' }}>
              {remLeft}/{remMax} left
            </div>
            <div style={{ fontSize: 9, color: '#9ca3af' }}>today</div>
          </div>
        </div>

        {/* Messages area */}
        <div ref={msgAreaRef} onClick={() => setCtxMenu(null)} style={{ flex: 1, overflowY: 'auto', padding: '10px 0', background: '#f0f2f5', scrollBehavior: 'smooth' }}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
              <div style={{ fontSize: 32, marginBottom: 8, color: '#d1d5db' }}><MessageOutlined /></div>
              <div style={{ fontSize: 13 }}>
                {activeChat === 'global' ? 'RAJPUROHIT community में पहला message लिखें!' : activeChat === 'village' ? `${villageName || 'गाँव'} ग्रुप में पहला message लिखें!` : 'बातचीत शुरू करें!'}
              </div>
            </div>
          )}
          {/* Pending request banner */}
          {isPendingSent && (
            <div style={{ margin: '16px 14px 8px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><ClockCircleOutlined /> Message Request Sent</div>
              <div style={{ fontSize: 11, color: '#b45309' }}>
                You can send up to 3 messages. Chat will open fully once they accept.
              </div>
            </div>
          )}
          {messages.map(m => <MsgBubble key={m.id} m={m} />)}
          {typing && activeChat === 'global' && (
            <div style={{ padding: '4px 16px', fontSize: 11, color: '#65676b', fontStyle: 'italic' }}>कोई लिख रहा है…</div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        <div style={{ padding: '10px 12px', background: '#fff', borderTop: '1px solid #e4e6eb', flexShrink: 0 }}>
          {remLeft <= 0 ? (
            <div style={{ textAlign: 'center', padding: '10px', background: '#fef2f2', borderRadius: 10, fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
              Today's message limit is over. Please chat again tomorrow. 🙏
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {myPhoto
                ? <img src={myPhoto} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                : <div style={{ width: 32, height: 32, borderRadius: '50%', background: M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{ini(myName)}</div>
              }
              <div style={{ flex: 1, background: '#f0f2f5', borderRadius: 22, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <textarea
                  ref={inputRef}
                  value={text}
                  onChange={e => {
                    setText(e.target.value.slice(0, 500));
                    if (activeChat === 'global' && socket) socket.emit('member_typing');
                  }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder={activeChat === 'global' ? 'Community को message लिखें…' : activeChat === 'village' ? `${villageName} ग्रुप में लिखें...` : `${activeContact?.name?.split(' ')[0] || 'Contact'} को message लिखें…`}
                  rows={1}
                  style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, resize: 'none', fontFamily: 'inherit', lineHeight: 1.4 }}
                  onInput={e => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px'; }}
                />
                <button onClick={sendMessage} disabled={!text.trim() || sending} style={{
                  background: 'none', border: 'none', cursor: text.trim() ? 'pointer' : 'default',
                  color: text.trim() ? M : '#bcc0c4', fontSize: 20, display: 'flex', alignItems: 'center', flexShrink: 0,
                }}>
                  {sending ? <Spin size="small" /> : <SendOutlined />}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

